'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { ComponentProps, useMemo, useRef, useState } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import useLogWebSectionViewed from '@alltrails/layout/hooks/useLogWebSectionViewed';
import SearchLoading from '@alltrails/reviews/components/SearchFilter/SearchLoading/SearchLoading';
import type { Review } from '@alltrails/types/Review';
import { LocationDetailsResponseType, PageFilterType, ReviewAndTrail, VisitorStats } from '@/types/locationDetailsPages/shared';
import { getAnalyticsPageType } from '@/utils/getAnalyticsPageType';
import layoutStyles from '../layouts/layoutStyles.module.scss';
import styles from './Reviews.module.scss';
import ReviewsList from './ReviewsList';
import { ReviewTitle } from './ReviewTitle';
import SocialProofing from './SocialProofing';

const ReviewFormModal = dynamic(() => import('@alltrails/reviews/components/NewReviewFormModal/NewReviewFormModal'));

type Props = {
  reviewsData: ReviewAndTrail[];
  mentionReviewSnippets?: Review[];
  locationName: string;
  type: LocationDetailsResponseType;
  visitorStats?: VisitorStats;
  pageFilterUid?: PageFilterType;
  seoTestActivityInTitle?: boolean;
  isBot?: boolean;
  isScrollLimitActive?: boolean;
  isSeoTestHideReviews?: boolean;
  seoTestLimitReviewsToThree?: boolean;
  seoTestFiveKeywordReviews?: boolean;
  seoKeywordReviews?: Review[];
};

const Reviews = ({
  reviewsData,
  mentionReviewSnippets = [],
  locationName,
  type,
  visitorStats,
  pageFilterUid,
  seoTestActivityInTitle,
  isBot,
  isScrollLimitActive,
  isSeoTestHideReviews,
  seoTestLimitReviewsToThree,
  seoTestFiveKeywordReviews,
  seoKeywordReviews
}: Props) => {
  const pageType = getAnalyticsPageType(type);
  const user = useUser();
  const reviews = useMemo(() => reviewsData?.map?.(review => review.review_snippet) ?? [], [reviewsData]);
  const [isReviewFormOpen, toggleReviewForm] = useState(false);
  const [reviewFormProps, setReviewFormProps] = useState<ComponentProps<typeof ReviewFormModal>>();
  const [currentTrailId, setCurrentTrailId] = useState<number | undefined>();
  const openReviewFormModal = (formProps: ComponentProps<typeof ReviewFormModal>) => {
    setReviewFormProps(formProps);
    toggleReviewForm(true);
    setCurrentTrailId(formProps.trailId);
  };
  const ref = useRef<HTMLDivElement>(null);
  useLogWebSectionViewed(ref, 'Trail Reviews', pageType);

  const hideReviews = isScrollLimitActive && !user && isSeoTestHideReviews;
  const shouldLimitReviews = !user && seoTestLimitReviewsToThree;

  const [displayReviews, displayMentionReviews] = useMemo(() => {
    let displayReviews = reviews;
    let displayMentionReviews = mentionReviewSnippets;

    if (shouldLimitReviews) {
      displayReviews = reviews.slice(0, 3);
      displayMentionReviews = [];
    }

    if (seoTestFiveKeywordReviews) {
      const seoReviewIds = seoKeywordReviews?.map(review => review.id) ?? [];
      const fillerReviews = (displayReviews ?? []).filter(review => !seoReviewIds.includes(review.id));
      displayReviews = [...(seoKeywordReviews ?? []), ...fillerReviews].slice(0, 5);
      displayMentionReviews = [];
    }

    return [displayReviews, displayMentionReviews];
  }, [reviews, mentionReviewSnippets, shouldLimitReviews, seoTestFiveKeywordReviews, seoKeywordReviews]);

  return (
    <div ref={ref}>
      <div className={styles.container}>
        <span className={styles.overview}>
          <h2 className={styles.reviewTitle}>
            <ReviewTitle locationName={locationName} type={type} pageFilterUid={pageFilterUid} seoTestActivityInTitle={seoTestActivityInTitle} />
          </h2>
          {visitorStats && type === 'park' && <SocialProofing reviews={[...mentionReviewSnippets, ...reviews]} visitorStats={visitorStats} />}
        </span>
        <div className={classNames(layoutStyles.col1, styles.desktopSpacer)} />
        {hideReviews ? (
          <div className={styles.ugcContainer}>
            <SearchLoading repeatNum={3} />
          </div>
        ) : (
          <ReviewsList
            type={type}
            locationName={locationName}
            openReviewFormModal={openReviewFormModal}
            reviews={displayReviews}
            mentionReviews={displayMentionReviews}
            isBot={isBot}
          />
        )}
        {user && isReviewFormOpen && (
          <ReviewFormModal
            activity={reviewFormProps?.activity}
            associatedRecording={reviewFormProps?.associatedRecording}
            associatedTrailReviewId={reviewFormProps?.associatedTrailReviewId}
            closeReviewModal={() => toggleReviewForm(false)}
            comment={reviewFormProps?.comment}
            comment_source={reviewFormProps?.comment_source}
            date={reviewFormProps?.date}
            difficulty={reviewFormProps?.difficulty}
            id={reviewFormProps?.id}
            infoAttributes={reviewFormProps?.infoAttributes}
            isActivity={reviewFormProps?.isActivity}
            obstacles={reviewFormProps?.obstacles}
            rating={reviewFormProps?.rating}
            ratingAttributes={reviewFormProps?.ratingAttributes}
            reviewSource={reviewFormProps?.reviewSource}
            trailId={currentTrailId}
            user={user}
          />
        )}
      </div>
    </div>
  );
};

export default Reviews;
